import { useEffect, useState } from "react";
import { Printer, Download } from "lucide-react";
import api from "../../lib/api";
import Modal from "../Modal";
import PhaseChip from "../PhaseChip";
import { uploadUrl } from "../../lib/uploads";
import { printElement } from "../../lib/printArea";
import useServerPdf from "../../lib/useServerPdf";
import { useBranding } from "../../context/TenantBrandingContext";

const FALLBACK_LOGO = "/brand/rogue-white-blue.png";

/**
 * Document view of a Job Card — print/PDF friendly.
 * Header appears once (natural flow, no repeating header).
 * Tasks (checklist) show full status + comments. Sign-off lives at the end
 * of the document, so it lands on the last page.
 */
export default function JobCardPrintModal({ job, customerName, onClose }) {
  const { branding } = useBranding();
  // [Iter 290zp.13t] PDFs always render on white paper, so the light
  // variant is the right choice regardless of the UI's current theme.
  // Fall back to logo_url for tenants without a light variant uploaded.
  const BRAND_LOGO = branding?.logo_url_light || branding?.logo_url || FALLBACK_LOGO;
  const BRAND_NAME = branding?.app_name || "Rogue";
  const BRAND_COMPANY = branding?.company_name || BRAND_NAME;
  const [users, setUsers] = useState([]);
  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data)).catch(() => setUsers([]));
  }, []);

  const shortId = job?.short_id || job?.id?.slice(0, 8) || "jobcard";
  const { download: downloadPdf, busy: downloadingPdf } = useServerPdf({
    endpoint: job?.id ? `/jobcards/${job.id}/pdf` : null,
    filename: `${BRAND_NAME}_JobCard_${shortId}.pdf`,
  });

  if (!job) return null;
  const userName = (id) =>
    users.find((u) => u.id === id)?.name || (id ? id.slice(0, 8) : null);

  const techName = userName(job.technician_id);
  const teamNames = (job.staff_ids || [])
    .filter((id) => id !== job.technician_id)
    .map(userName)
    .filter(Boolean);

  // [Iter 327] `uploadUrl()` normalises bare-id / path / stale full URL.
  const photoUrl = (p) => (p?.startsWith("data:") ? p : uploadUrl(p) || p);
  const sigUrl = (sig) => {
    if (!sig?.signature) return null;
    return sig.signature.startsWith("data:") ? sig.signature : uploadUrl(sig.signature) || sig.signature;
  };

  const handlePrint = () =>
    printElement("jc-print-area", {
      title: `Job Card #${job.short_id || job.id?.slice(0, 8) || ""}`,
    });

  // Group checklist by status: done first, then pending, skipped last.
  const checklist = job.checklist || [];
  const done = checklist.filter((c) => c.status === "done");
  const pending = checklist.filter(
    (c) => !c.status || c.status === "pending" || c.status === "in_progress",
  );
  const skipped = checklist.filter((c) => c.status === "skipped");

  return (
    <>
      <Modal
        open
        onClose={onClose}
        title="Job Card · Document view"
        testId="jobcard-print-modal"
        maxWidth="max-w-4xl"
      >
        <div className="space-y-3" data-testid="jobcard-print-content">
          {/* Toolbar */}
          <div className="no-print flex justify-end gap-2 -mt-2">
            <button
              type="button"
              onClick={downloadPdf}
              disabled={downloadingPdf}
              className="hv-btn hv-btn-primary"
              data-testid="jobcard-download-pdf-btn"
              title="Download a server-rendered PDF — always light mode, deterministic layout"
            >
              <Download className="w-4 h-4" />
              {downloadingPdf ? "Generating…" : "Download PDF"}
            </button>
            <button
              type="button"
              onClick={handlePrint}
              className="hv-btn hv-btn-ghost"
              data-testid="jobcard-print-btn"
              title="Print in browser — always renders in light mode"
            >
              <Printer className="w-4 h-4" /> Print
            </button>
          </div>

          <div
            id="jc-print-area"
            className="pcard border border-white/10 rounded-md p-5 md:p-6 bg-[#0a0b0e]"
          >
            {/* ---------- Compact header (page 1 only via natural flow) ---------- */}
            <div className="pheader-bg flex items-start justify-between gap-3 pb-2 border-b pdivider border-white/10">
              <div className="flex items-center gap-3">
                <img
                  src={BRAND_LOGO}
                  alt={BRAND_NAME}
                  className="h-10 md:h-12 w-auto object-contain"
                />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] paccent text-[#1fb6c1] font-bold">
                    {BRAND_NAME} · Job Card
                  </div>
                  <div className="font-display text-lg md:text-xl font-bold ptext leading-tight">
                    {job.title}
                  </div>
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-display text-2xl md:text-3xl font-bold paccent text-[#1fb6c1] leading-none">
                  #{job.short_id || job.id?.slice(0, 8)}
                </div>
                {job.external_ref && (
                  <div className="text-[10px] font-mono pmeta text-[#dde3ec] mt-0.5">
                    {job.external_ref}
                  </div>
                )}
              </div>
            </div>

            {/* Meta grid — compact, label/value pairs */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1.5 mt-3 text-sm avoid-break">
              <Meta label="Customer" value={customerName} />
              <Meta
                label="Phase"
                valueEl={
                  <span className="pchip">
                    <PhaseChip value={job.phase} />
                  </span>
                }
              />
              <Meta
                label="Scheduled"
                value={
                  job.scheduled_at ? new Date(job.scheduled_at).toLocaleString('en-GB') : "—"
                }
              />
              <Meta label="Technician" value={techName || "—"} />
              <Meta
                label="Team members"
                value={teamNames.length ? teamNames.join(", ") : "—"}
              />
              <Meta
                label="On-site complete"
                value={
                  job.on_site_complete_date
                    ? new Date(job.on_site_complete_date).toLocaleString('en-GB')
                    : "—"
                }
              />
              {job.priority && <Meta label="Priority" value={job.priority} />}
              {job.required_skills?.length ? (
                <Meta label="Skills" value={job.required_skills.join(", ")} />
              ) : null}
            </div>

            {/* Description */}
            {job.description && (
              <Section label="Description">
                <p className="text-sm whitespace-pre-wrap leading-relaxed ptext text-[#dde3ec]">
                  {job.description}
                </p>
              </Section>
            )}

            {/* Special instructions */}
            {job.special_instructions && (
              <Section label="Special instructions">
                <div className="px-3 py-2 rounded border border-amber-500/40 bg-amber-500/10 ptext text-[#dde3ec] text-sm whitespace-pre-wrap leading-relaxed avoid-break">
                  {job.special_instructions}
                </div>
              </Section>
            )}

            {/* Tasks — Done */}
            {done.length > 0 && (
              <TaskGroup label={`Completed tasks (${done.length})`} items={done} statusColor="#10b981" photoUrl={photoUrl} userName={userName} />
            )}
            {pending.length > 0 && (
              <TaskGroup label={`Pending tasks (${pending.length})`} items={pending} statusColor="#f59e0b" photoUrl={photoUrl} userName={userName} />
            )}
            {skipped.length > 0 && (
              <TaskGroup label={`Skipped tasks (${skipped.length})`} items={skipped} statusColor="#647087" photoUrl={photoUrl} userName={userName} />
            )}

            {/* Photos (job-level) */}
            {!!(job.photos || []).length && (
              <Section label="Photos">
                <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                  {job.photos.map((p, i) => (
                    <img
                      key={i}
                      src={photoUrl(p)}
                      alt={`photo-${i}`}
                      className="rounded border border-white/10 max-h-40 w-full object-cover avoid-break"
                    />
                  ))}
                </div>
              </Section>
            )}

            {/* Sign-offs — last block, sits at end (last page) */}
            <Section label="Sign-offs">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 avoid-break">
                <Sign label="Technician" sig={job.tech_signoff} sigUrl={sigUrl} />
                <Sign label="Client" sig={job.client_signoff} sigUrl={sigUrl} />
                <Sign label={`${BRAND_COMPANY} manager`} sig={job.mgmt_signoff} sigUrl={sigUrl} />
              </div>
            </Section>

            {/* Footer */}
            <div className="pt-3 mt-4 border-t pdivider border-white/10 text-center">
              <div className="text-[10px] uppercase tracking-[0.25em] pmeta text-[#dde3ec]">
                Generated {new Date().toLocaleString('en-GB')} ·{" "}
                <span className="paccent text-[#1fb6c1] font-bold">
                  {BRAND_NAME}
                </span>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
}

function Meta({ label, value, valueEl }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-[10px] uppercase tracking-[0.18em] plabel text-[#1fb6c1] font-bold shrink-0 min-w-[78px]">
        {label}
      </span>
      {valueEl ? (
        <span className="ptext text-[#dde3ec]">{valueEl}</span>
      ) : (
        <span className="text-sm ptext text-[#dde3ec] break-words">{value || "—"}</span>
      )}
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div className="mt-4">
      <div className="text-[10px] uppercase tracking-[0.2em] plabel text-[#1fb6c1] mb-2 font-bold">
        {label}
      </div>
      {children}
    </div>
  );
}

function TaskGroup({ label, items, statusColor, photoUrl, userName }) {
  return (
    <Section label={label}>
      <ul className="space-y-2">
        {items.map((c, i) => (
          <li
            key={c.id || i}
            className="pcard border rounded p-2.5 avoid-break"
            style={{ borderColor: `${statusColor}66`, background: `${statusColor}10` }}
          >
            <div className="flex items-start justify-between gap-2 text-sm">
              <span className="font-medium ptext text-[#dde3ec] flex-1">
                {i + 1}. {c.title || c.label || c.text || "Item"}
              </span>
              <span
                className="pchip text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border whitespace-nowrap shrink-0 font-bold"
                style={{ borderColor: statusColor, color: statusColor, background: `${statusColor}15` }}
              >
                {c.status || "pending"}
              </span>
            </div>
            {c.notes && (
              <div className="text-xs ptext text-[#dde3ec] mt-1 whitespace-pre-wrap leading-relaxed">
                {c.notes}
              </div>
            )}
            {!!(c.comments || []).length && (
              <ul className="mt-2 space-y-1 border-l-2 pl-2 pdivider border-white/10">
                {c.comments.map((cm) => (
                  <li key={cm.id} className="text-[11px] ptext text-[#dde3ec]">
                    <span className="plabel paccent text-[#1fb6c1] font-bold">
                      {userName(cm.by_user_id) || cm.by_user_name || "—"}:
                    </span>{" "}
                    {cm.text}
                    {cm.created_at && (
                      <span className="pmeta text-[#dde3ec]">
                        {" · "}
                        {new Date(cm.created_at).toLocaleString('en-GB')}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {!!(c.photos || []).length && (
              <div className="mt-2 grid grid-cols-3 gap-2">
                {c.photos.map((p, j) => (
                  <img
                    key={j}
                    src={photoUrl(p)}
                    alt={`item-${i}-${j}`}
                    className="rounded border border-white/10 max-h-28 w-full object-cover"
                  />
                ))}
              </div>
            )}
          </li>
        ))}
      </ul>
    </Section>
  );
}

function Sign({ label, sig, sigUrl }) {
  if (!sig) {
    return (
      <div className="border border-dashed pdivider border-white/15 rounded p-3 min-h-[80px]">
        <div className="text-[10px] uppercase tracking-wider plabel text-[#1fb6c1] font-bold">
          {label}
        </div>
        <div className="text-xs pmeta text-[#dde3ec] italic mt-2">Not signed</div>
      </div>
    );
  }
  const url = sigUrl(sig);
  return (
    <div className="pcard border border-white/15 rounded p-3 avoid-break">
      <div className="text-[10px] uppercase tracking-wider plabel text-[#1fb6c1] font-bold">
        {label}
      </div>
      {url && (
        <img
          src={url}
          alt={`${label} signature`}
          className="mt-1 max-h-14 mx-auto bg-white rounded"
        />
      )}
      <div className="text-xs font-medium mt-1 text-center ptext text-[#dde3ec]">
        {sig.name || "—"}
      </div>
      {sig.signed_at && (
        <div className="text-[9px] text-center pmeta text-[#dde3ec]">
          {new Date(sig.signed_at).toLocaleString('en-GB')}
        </div>
      )}
    </div>
  );
}
